import { DefaultMap, definedMap } from "@glideapps/ts-necessities";
import type { DataStoreEnvironmentAccessors } from ".";
import { DurableStorageControllerBase } from ".";
import type { PrimitiveValue } from "../../../../components/computation-types";
import type { RowIndex } from "../../../../Database";
import { areRowIndexesConflicting } from "../../../../Database";
import type { TableName } from "../../../../database-strings";
import {
    ActionKind,
    actionsMetadataCollectionForDevice,
    isSample,
    makeActionDocumentPath,
} from "../../../../database-strings";
import { logError, logInfo } from "../../../../debug-print";
import type { OfflineQueue } from "../../../types";
import type { AddRowActionWithMetadataArguments } from "../crud-actions";
import { postAddRowToTableActionWithMetadata, postDeleteRowActionWithMetadata } from "../crud-actions";
import { generateFirestoreDocumentID } from "../id-generator";
import type { AddRowQueueItem } from "../types";

export abstract class ConsistentDurableStorageController extends DurableStorageControllerBase {
    private readonly _lastActionPaths: DefaultMap<string, [RowIndex, string][]> = new DefaultMap(() => []);
    private _offlineQueue: OfflineQueue<AddRowQueueItem> | undefined;

    constructor(accessors: DataStoreEnvironmentAccessors, deviceIDOverride: string | undefined, isBuilder: boolean) {
        super(accessors, deviceIDOverride, isBuilder);
        this.updateOfflineQueue();
    }

    protected abstract confirmAction(
        tableName: TableName,
        rowIndex: RowIndex,
        jobID: string,
        confirmedAtSerial: number
    ): Promise<void>;

    private updateOfflineQueue(): void {
        this._offlineQueue?.retire();
        this._offlineQueue = undefined;

        this._offlineQueue = this._accessors.makeOfflineQueue(
            this._accessors.appID() + "-actions",
            this.processOfflineQueue
        );
        void this.processOfflineQueue();
    }

    private async postAddRow(actionData: AddRowActionWithMetadataArguments): Promise<void> {
        const db = this._accessors.database();
        await db.runTransaction(
            "postAddRowToTableActionWithMetadata",
            async txn => await postAddRowToTableActionWithMetadata(txn, actionData)
        );
        logInfo("addRowToTable job", actionData.deviceID, actionData.jobID);
    }

    private readonly processOfflineQueue = async (): Promise<void> => {
        const queue = this._offlineQueue;
        if (queue === undefined) return;

        const appUserID = this._accessors.appUserID();
        const authID = await this._accessors.appFacilities().getAuthUserID();

        await queue.processIfOnline(async item => {
            if (item.data.appUserID !== appUserID || item.data.authID !== authID) return false;
            await this.postAddRow(item.data);
            return true;
        });
    };

    // Takes the path of the new action for a row and returns the path of the
    // last action that modified that row.
    private replaceLastActionPath(tableName: TableName, rowIndex: RowIndex, newActionPath: string): string | undefined {
        const entries = this._lastActionPaths.get(tableName.name);
        for (let i = 0; i < entries.length; i++) {
            const [ri, p] = entries[i];
            if (!areRowIndexesConflicting(rowIndex, ri)) continue;

            entries[i] = [ri, newActionPath];
            return p;
        }

        entries.push([rowIndex, newActionPath]);
        return undefined;
    }

    protected makeJobIDAndDependsOn(
        tableName: TableName,
        rowIndex: RowIndex | undefined,
        actionKind: ActionKind,
        jobID?: string
    ): { jobID: string; dependsOn: string | undefined } {
        const appID = this._accessors.appID();
        if (jobID === undefined) {
            jobID = generateFirestoreDocumentID();
        }
        const dependsOn = definedMap(rowIndex, ri =>
            this.replaceLastActionPath(tableName, ri, `${makeActionDocumentPath(appID, actionKind)}/${jobID}`)
        );
        return { jobID, dependsOn };
    }

    protected listenForActionConfirmation(
        tableName: TableName,
        rowIndex: RowIndex,
        jobID: string,
        // We call either `onConfirm` (if it's set) or `this.confirmAction`,
        // but not both.  No reason other than avoiding more special casing at
        // the moment.  We only need `onConfirm` for delete action, which
        // doesn't need `this.confirmAction`.
        onConfirm: ((serial: number) => void) | undefined
    ): void {
        const { deviceID } = this;
        const db = this._accessors.database();

        const unlisten = db.listenToDocument(actionsMetadataCollectionForDevice(deviceID), jobID, async currentDoc => {
            const confirmedAtSerial = currentDoc.response?.actionWriteback?.confirmedAtSerial;
            if (typeof confirmedAtSerial !== "number") return;

            unlisten();

            if (onConfirm === undefined) {
                await this.confirmAction(tableName, rowIndex, jobID, confirmedAtSerial);
            } else {
                onConfirm(confirmedAtSerial);
            }
        });
    }

    public async addRowToTable(
        tableName: TableName,
        rowIndex: RowIndex | undefined,
        values: Record<string, PrimitiveValue>,
        fromDataEditor: boolean
    ): Promise<boolean> {
        const db = this._accessors.database();
        const appID = this._accessors.appID();
        const appUserID = this._accessors.appUserID();
        const appFacilities = this._accessors.appFacilities();
        const fields = this.convertColumnValuesToFields(tableName, values);

        await db.enterNetworkConnectionLock();

        try {
            const authID = await appFacilities.getAuthUserID();
            const { deviceID } = this;
            const { jobID, dependsOn } = this.makeJobIDAndDependsOn(tableName, rowIndex, ActionKind.AddRowToTable);

            const actionData: AddRowActionWithMetadataArguments = {
                tableName,
                columnValues: fields,
                jobID,
                deviceID,
                dependsOn,
                appID,
                appUserID,
                authID,
                fromBuilder: this.isBuilder,
                fromDataEditor,
            };

            if (this._offlineQueue !== undefined) {
                this._offlineQueue.enqueue({ kind: "add-row", data: actionData });
                await this.processOfflineQueue();
            } else {
                await this.postAddRow(actionData);
            }
            return true;
        } catch (e: unknown) {
            logError("Posting add row to table failed", e);
            // We don't report write errors in sample apps
            return !isSample(appID);
        } finally {
            db.exitNetworkConnectionLock();
        }
    }

    public async deleteRowAtIndex(
        tableName: TableName,
        rowIndex: RowIndex,
        fromDataEditor: boolean,
        onConfirm: ((serial: number) => void) | undefined
    ): Promise<void> {
        const db = this._accessors.database();
        const appFacilities = this._accessors.appFacilities();
        const appID = this._accessors.appID();
        const appUserID = this._accessors.appUserID();

        await db.enterNetworkConnectionLock();
        try {
            const authID = await appFacilities.getAuthUserID();
            const { deviceID } = this;
            const { jobID, dependsOn } = this.makeJobIDAndDependsOn(tableName, rowIndex, ActionKind.DeleteRow);

            await db.runTransaction(
                "postDeleteRowActionWithMetadata",
                async txn =>
                    await postDeleteRowActionWithMetadata(txn, {
                        appID,
                        appUserID,
                        authID,
                        tableName,
                        rowIndex,
                        deviceID,
                        dependsOn,
                        jobID,
                        fromBuilder: this.isBuilder,
                        fromDataEditor,
                    })
            );
            logInfo("deleteRowAtIndex job", deviceID, jobID);

            if (onConfirm !== undefined) {
                this.listenForActionConfirmation(tableName, rowIndex, jobID, onConfirm);
            }
        } finally {
            db.exitNetworkConnectionLock();
        }
    }

    public appEnvironmentUpdated(): void {
        super.appEnvironmentUpdated();
        this.updateOfflineQueue();
    }

    public appUserChanged(): void {
        super.appUserChanged();
        this.updateOfflineQueue();
    }

    public retire(): void {
        this._offlineQueue?.retire();
    }
}
